import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { IconClose } from './Icon';

/**
 * Modal — accessible dialog wrapper.
 *
 *   - Esc closes (already had this).
 *   - Click on backdrop closes (existing).
 *   - Focus enters the dialog on open and is restored on close.
 *   - Tab / Shift+Tab cycles within the dialog (focus trap).
 *   - role="dialog" + aria-modal + aria-labelledby for the title.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 560,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // 'closing' adds a [data-closing] attr to the dialog so the CSS
  // exit animation runs. We delay onClose() by ~160ms (matching the
  // animation duration in index.css) so users see the dialog slide
  // away instead of vanishing.
  const [closing, setClosing] = useState(false);
  const requestClose = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      setClosing(false);
      onClose();
    }, 160);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Pull focus into the dialog as soon as it mounts. We prefer the
    // first focusable inside the content; fall back to the dialog
    // itself.
    queueMicrotask(() => {
      const dlg = dialogRef.current;
      if (!dlg) return;
      const first = dlg.querySelector<HTMLElement>(focusableSelector);
      (first ?? dlg).focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        requestClose();
        return;
      }
      if (e.key === 'Tab') {
        const dlg = dialogRef.current;
        if (!dlg) return;
        const focusables = Array.from(
          dlg.querySelectorAll<HTMLElement>(focusableSelector),
        ).filter(isVisible);
        if (focusables.length === 0) {
          // Keep focus pinned on the dialog itself.
          e.preventDefault();
          dlg.focus();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || !dlg.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      // Restore focus where the user was when we opened.
      const prev = previouslyFocusedRef.current;
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [open, onClose]);

  if (!open && !closing) return null;

  return (
    <div
      data-modal-backdrop
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/55 backdrop-blur-sm sm:items-center"
      onClick={requestClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        data-closing={closing ? 'true' : undefined}
        className="panel w-full max-h-[88vh] overflow-auto rounded-t-2xl border-t-0 sm:rounded-2xl sm:border-t sm:w-auto"
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-ink-rim/60 p-4">
          <div id="modal-title" className="min-w-0 text-base font-medium">
            {title}
          </div>
          <button
            type="button"
            className="btn btn-ghost -mr-1.5 -my-1.5 p-1.5"
            onClick={requestClose}
            aria-label="Close"
          >
            <IconClose size={16} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const focusableSelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  // Bare-minimum visibility check: getBoundingClientRect would be
  // more thorough but overkill for the dialog's contents.
  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  return !style || (style.visibility !== 'hidden' && style.display !== 'none');
}
